import { createRouteBadgeTextRenderer } from "./routeBadgeText.js";

export const routeBadgeCanvasSize = 96;
export const routeBadgePixelRatio = 2;
const routeBadgeCacheDbName = "catchy-route-badge-cache";
const routeBadgeCacheStoreName = "image-data";
const routeBadgeImageDataCache = new globalThis.Map();
let routeBadgeCacheDbPromise = null;
let routeBadgeOutlineFontPromise = null;
const micromobilityMarkerAssetVersion =
  process.env.NODE_ENV === "development" ? `dev-${Date.now()}` : "2026-09-11-1";

function canvasToImageData(canvas) {
  const ctx = canvas.getContext("2d");
  return ctx?.getImageData(0, 0, canvas.width, canvas.height) ?? null;
}

function imageLoadFromUrl(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = "async";
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = url;
  });
}

async function outlineRouteBadgeText(svg) {
  const document = new DOMParser().parseFromString(svg, "image/svg+xml");
  const textElements = [...document.querySelectorAll("text")];
  if (!textElements.length) return svg;

  if (!routeBadgeOutlineFontPromise) {
    routeBadgeOutlineFontPromise = Promise.all([
      import("opentype.js"),
      fetch("/fonts/BusVF.woff", { cache: "force-cache" }).then((response) => {
        if (!response.ok) throw new Error("Failed to load Bus route badge font");
        return response.arrayBuffer();
      }),
    ])
      .then(([module, buffer]) => (module.default ?? module).parse(buffer))
      .catch((error) => {
        // A transient fetch failure must not poison later attempts.
        routeBadgeOutlineFontPromise = null;
        throw error;
      });
  }
  const font = await routeBadgeOutlineFontPromise;
  const [, , width, height] = document.documentElement
    .getAttribute("viewBox")
    .split(/\s+/)
    .map(Number);
  const renderText = createRouteBadgeTextRenderer(font, { width, height });
  for (const text of textElements) {
    const pathSvg = renderText({
      label: text.textContent,
      fill: text.getAttribute("fill"),
      x: text.getAttribute("x"),
      y: text.getAttribute("y"),
      fontSize: text.getAttribute("font-size"),
      fontWeight: text.getAttribute("font-weight"),
      fontWidth: text.getAttribute("font-stretch"),
      letterSpacing: text.getAttribute("letter-spacing"),
    });
    const path = new DOMParser().parseFromString(
      `<svg xmlns="http://www.w3.org/2000/svg">${pathSvg}</svg>`,
      "image/svg+xml",
    ).documentElement.firstElementChild;
    text.replaceWith(document.importNode(path, true));
  }
  return new XMLSerializer().serializeToString(document);
}

const getRouteBadgeCacheKey = (badgeSpec) =>
  [
    "v15-outline",
    badgeSpec.imageId,
    badgeSpec.routeId,
    badgeSpec.routeLabel,
    badgeSpec.routeColor,
    badgeSpec.routeTextColor,
    badgeSpec.routeType,
    routeBadgeCanvasSize,
    routeBadgePixelRatio,
  ].join("|");

function openRouteBadgeCacheDb() {
  if (typeof indexedDB === "undefined") return null;
  if (routeBadgeCacheDbPromise) return routeBadgeCacheDbPromise;

  routeBadgeCacheDbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(routeBadgeCacheDbName, 1);

    request.onupgradeneeded = () => {
      request.result.createObjectStore(routeBadgeCacheStoreName);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  return routeBadgeCacheDbPromise;
}

async function getPersistentRouteBadgeImageData(cacheKey) {
  if (typeof ImageData === "undefined") return null;

  try {
    const db = await openRouteBadgeCacheDb();
    if (!db) return null;

    return await new Promise((resolve, reject) => {
      const transaction = db.transaction(routeBadgeCacheStoreName, "readonly");
      const request = transaction
        .objectStore(routeBadgeCacheStoreName)
        .get(cacheKey);

      request.onsuccess = () => {
        const record = request.result;
        if (!record?.data || !record.width || !record.height) {
          resolve(null);
          return;
        }

        resolve(
          new ImageData(
            new Uint8ClampedArray(record.data),
            record.width,
            record.height,
          ),
        );
      };
      request.onerror = () => reject(request.error);
    });
  } catch {
    return null;
  }
}

async function setPersistentRouteBadgeImageData(cacheKey, imageData) {
  try {
    const db = await openRouteBadgeCacheDb();
    if (!db) return;

    const data = imageData.data.buffer.slice(0);
    await new Promise((resolve, reject) => {
      const transaction = db.transaction(routeBadgeCacheStoreName, "readwrite");
      transaction.objectStore(routeBadgeCacheStoreName).put(
        {
          width: imageData.width,
          height: imageData.height,
          data,
        },
        cacheKey,
      );
      transaction.oncomplete = resolve;
      transaction.onerror = () => reject(transaction.error);
    });
  } catch {
    // Persistent cache is opportunistic; memory cache and regeneration remain safe.
  }
}

export async function routeBadgeSvgToImageData(
  svg,
  { size = routeBadgeCanvasSize, pixelRatio = routeBadgePixelRatio } = {},
) {
  const canvas = document.createElement("canvas");
  canvas.width = size * pixelRatio;
  canvas.height = size * pixelRatio;

  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  // SVG image onload does not guarantee embedded font glyphs have painted.
  const outlinedSvg = await outlineRouteBadgeText(svg);
  const blob = new Blob([outlinedSvg], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  try {
    const image = await imageLoadFromUrl(url);

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

    if (typeof image.close === "function") {
      image.close();
    }

    return canvasToImageData(canvas);
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function imageUrlToImageData(
  url,
  {
    width = routeBadgeCanvasSize,
    height = routeBadgeCanvasSize,
    pixelRatio = routeBadgePixelRatio,
  } = {},
) {
  const canvas = document.createElement("canvas");
  canvas.width = width * pixelRatio;
  canvas.height = height * pixelRatio;

  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const image = await imageLoadFromUrl(url);

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  if (typeof image.close === "function") {
    image.close();
  }

  return canvasToImageData(canvas);
}

export const micromobilityMarkerImageIds = [
  "micromobility-flamingo-bike",
  "micromobility-flamingo-scooter",
  "micromobility-jolt-scooter",
  "micromobility-flamingo-bike-group",
  "micromobility-flamingo-scooter-group",
  "micromobility-jolt-scooter-group",
  "micromobility-flamingo-bike-active",
  "micromobility-flamingo-scooter-active",
  "micromobility-jolt-scooter-active",
];

function withMicromobilityMarkerAssetVersion(url) {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}v=${micromobilityMarkerAssetVersion}`;
}

const micromobilityGroupMarkerSpecs = {
  "micromobility-flamingo-bike-group": {
    url: "/icons/micromobility/marker-group-bike-flamingo.png",
    width: 24,
    height: 24,
    textColor: "#FF206E",
  },
  "micromobility-flamingo-scooter-group": {
    url: "/icons/micromobility/marker-group-scooter-flamingo.png",
    width: 24,
    height: 24,
    textColor: "#FFFFFF",
  },
  "micromobility-jolt-scooter-group": {
    url: "/icons/micromobility/marker-group-scooter-jolt.png",
    width: 24,
    height: 24,
    textColor: "#FFFFFF",
  },
};

const micromobilityGroupMarkerImageIdPattern =
  /^(micromobility-(?:flamingo-bike|flamingo-scooter|jolt-scooter)-group)-(\d+)$/;

async function groupMarkerImageToImageData(
  {
    url,
    width = routeBadgeCanvasSize,
    height = routeBadgeCanvasSize,
    count,
    textColor,
  },
  { pixelRatio = routeBadgePixelRatio } = {},
) {
  const canvas = document.createElement("canvas");
  canvas.width = width * pixelRatio;
  canvas.height = height * pixelRatio;

  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const image = await imageLoadFromUrl(url);

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  if (typeof image.close === "function") {
    image.close();
  }

  if (document.fonts?.ready) {
    await document.fonts.ready;
  }

  const label = String(count);
  const fontSize =
    label.length > 2
      ? width * 0.34
      : label.length > 1
        ? width * 0.42
        : width * 0.5;
  ctx.font = `600 ${fontSize * pixelRatio}px Bus, sans-serif`;
  ctx.fillStyle = textColor;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(
    label,
    (width * pixelRatio) / 2,
    (height * pixelRatio) / 2 + pixelRatio * 0.5,
  );

  return canvasToImageData(canvas);
}

function getMicromobilityGroupMarkerSpecs(scooterPositions) {
  const specs = new globalThis.Map();

  for (const feature of scooterPositions?.features || []) {
    const imageId = feature?.properties?.markerImageId;
    const match =
      typeof imageId === "string"
        ? imageId.match(micromobilityGroupMarkerImageIdPattern)
        : null;

    if (!match) {
      continue;
    }

    const [, baseImageId, count] = match;
    const baseSpec = micromobilityGroupMarkerSpecs[baseImageId];

    if (baseSpec) {
      specs.set(imageId, {
        ...baseSpec,
        imageId,
        count,
      });
    }
  }

  return [...specs.values()];
}

function mapHasImage(mapInstance, imageId) {
  if (!mapInstance?.style?.getImage) {
    return false;
  }

  return mapInstance.hasImage(imageId);
}

function canAddMapImage(mapInstance) {
  return Boolean(mapInstance?.style?.addImage);
}

export async function ensureMicromobilityMarkerImages(
  mapInstance,
  scooterPositions,
) {
  if (!mapInstance) {
    return false;
  }

  const markerSpecs = [
    {
      imageId: "micromobility-flamingo-bike",
      url: "/icons/micromobility/marker-bike-flamingo.png",
      width: 24,
      height: 24,
    },
    {
      imageId: "micromobility-flamingo-scooter",
      url: "/icons/micromobility/marker-scooter-flamingo.png",
      width: 24,
      height: 24,
    },
    {
      imageId: "micromobility-jolt-scooter",
      url: "/icons/micromobility/marker-scooter-jolt.png",
      width: 24,
      height: 24,
    },
    {
      imageId: "micromobility-flamingo-bike-group",
      url: "/icons/micromobility/marker-group-bike-flamingo.png",
      width: 24,
      height: 24,
    },
    {
      imageId: "micromobility-flamingo-scooter-group",
      url: "/icons/micromobility/marker-group-scooter-flamingo.png",
      width: 24,
      height: 24,
    },
    {
      imageId: "micromobility-jolt-scooter-group",
      url: "/icons/micromobility/marker-group-scooter-jolt.png",
      width: 24,
      height: 24,
    },
    {
      imageId: "micromobility-flamingo-bike-active",
      url: "/icons/micromobility/marker-active-bike-flamingo.png",
      width: 30,
      height: 42,
    },
    {
      imageId: "micromobility-flamingo-scooter-active",
      url: "/icons/micromobility/marker-active-scooter-flamingo.png",
      width: 30,
      height: 42,
    },
    {
      imageId: "micromobility-jolt-scooter-active",
      url: "/icons/micromobility/marker-active-scooter-jolt.png",
      width: 30,
      height: 42,
    },
    ...getMicromobilityGroupMarkerSpecs(scooterPositions),
  ];

  let imagesAdded = false;
  await Promise.all(
    markerSpecs.map(async (markerSpec) => {
      if (mapHasImage(mapInstance, markerSpec.imageId)) {
        return;
      }

      const imageUrl = withMicromobilityMarkerAssetVersion(markerSpec.url);
      const imageData =
        markerSpec.count == null
          ? await imageUrlToImageData(imageUrl, {
              width: markerSpec.width,
              height: markerSpec.height,
              pixelRatio: routeBadgePixelRatio,
            })
          : await groupMarkerImageToImageData(
              {
                url: imageUrl,
                width: markerSpec.width,
                height: markerSpec.height,
                count: markerSpec.count,
                textColor: markerSpec.textColor,
              },
              { pixelRatio: routeBadgePixelRatio },
            );

      if (
        !imageData ||
        !canAddMapImage(mapInstance) ||
        mapHasImage(mapInstance, markerSpec.imageId)
      ) {
        return;
      }

      mapInstance.addImage(markerSpec.imageId, imageData, {
        pixelRatio: routeBadgePixelRatio,
      });
      imagesAdded = true;
    }),
  );
  return imagesAdded;
}

async function getRouteBadgeImageData({
  badgeSpec,
  createRouteBadgeSvg,
  useCache = true,
}) {
  const cacheKey = getRouteBadgeCacheKey(badgeSpec);

  if (useCache && routeBadgeImageDataCache.has(cacheKey)) {
    return routeBadgeImageDataCache.get(cacheKey);
  }

  if (useCache) {
    const persistedImageData = await getPersistentRouteBadgeImageData(cacheKey);
    if (persistedImageData) {
      routeBadgeImageDataCache.set(cacheKey, persistedImageData);
      return persistedImageData;
    }
  }

  const svg = createRouteBadgeSvg(badgeSpec);
  const imageData = await routeBadgeSvgToImageData(svg);

  if (useCache && imageData) {
    routeBadgeImageDataCache.set(cacheKey, imageData);
    void setPersistentRouteBadgeImageData(cacheKey, imageData);
  }

  return imageData;
}

export function getVehicleRouteBadgeSpecs(vehiclePositions) {
  const badgeSpecs = new globalThis.Map();

  for (const feature of vehiclePositions?.features || []) {
    const routeId = feature?.properties?.routeId;
    const routeBadgeId = feature?.properties?.routeBadgeId || routeId;
    const routeLabel = feature?.properties?.routeLabel;
    const routeColor = feature?.properties?.routeColor;
    const routeTextColor =
      feature?.properties?.routeTextColor ??
      feature?.properties?.route_text_color;
    const routeType = feature?.properties?.routeType;

    if (!routeBadgeId || !routeLabel) {
      continue;
    }

    badgeSpecs.set(routeBadgeId, {
      imageId: feature?.properties?.routeImageId || `route-${routeBadgeId}`,
      routeId: routeBadgeId,
      routeLabel,
      routeColor,
      routeTextColor,
      routeType,
    });
  }

  return [...badgeSpecs.values()];
}

export async function ensureVehicleRouteBadgeImages({
  mapInstance,
  vehiclePositions,
  pendingImageIds,
  createRouteBadgeSvg,
  concurrency = 4,
}) {
  if (!mapInstance || typeof createRouteBadgeSvg !== "function") {
    return false;
  }

  const badgeSpecs = getVehicleRouteBadgeSpecs(vehiclePositions);
  const queue = [...badgeSpecs];
  let imagesAdded = false;

  await Promise.all(
    Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
      while (queue.length > 0) {
        const badgeSpec = queue.shift();

        if (
          mapHasImage(mapInstance, badgeSpec.imageId) ||
          pendingImageIds.current.has(badgeSpec.imageId)
        ) {
          continue;
        }

        pendingImageIds.current.add(badgeSpec.imageId);

        try {
          const imageData = await getRouteBadgeImageData({
            badgeSpec,
            createRouteBadgeSvg,
          });
          if (
            !imageData ||
            !canAddMapImage(mapInstance) ||
            mapHasImage(mapInstance, badgeSpec.imageId)
          ) {
            continue;
          }

          mapInstance.addImage(badgeSpec.imageId, imageData, {
            pixelRatio: routeBadgePixelRatio,
          });
          imagesAdded = true;
        } finally {
          pendingImageIds.current.delete(badgeSpec.imageId);
        }
      }
    }),
  );
  return imagesAdded;
}

export async function benchmarkRouteBadgeGeneration({
  badgeSpecs,
  createRouteBadgeSvg,
  iterations = 1,
  useCache = false,
}) {
  const specs = Array.isArray(badgeSpecs) ? badgeSpecs : [];
  const startedAt = performance.now();
  let generated = 0;

  if (document.fonts?.ready) {
    await document.fonts.ready;
  }

  for (let iteration = 0; iteration < iterations; iteration += 1) {
    for (const spec of specs) {
      const imageData = await getRouteBadgeImageData({
        badgeSpec: spec,
        createRouteBadgeSvg,
        useCache,
      });

      if (imageData) {
        generated += 1;
      }
    }
  }

  const totalMs = performance.now() - startedAt;

  return {
    badgeCount: specs.length,
    generated,
    iterations,
    totalMs,
    averageMs: generated ? totalMs / generated : 0,
  };
}
